/*
 * @Author: Claude
 * @Date: 2025-11-08
 * @Description: 文件校验规则类型定义
 */
import { ExtendedUploadFile } from './index';

/**
 * 自定义校验函数
 * @param file - 待校验的文件
 * @param fileList - 完整的文件列表
 * @returns Promise<string | void> - 返回错误信息或 void
 */
export type ValidatorFunction = (
  file: File,
  fileList: ExtendedUploadFile[],
) => Promise<string | void> | string | void;

/** 校验失败提示展示模式 */
export type ValidationErrorDisplayMode = 'constraint' | 'raw';

/**
 * 文件类型匹配器
 * 用于判断规则是否适用于某个文件
 */
export type FileTypeMatcher =
  | string // MIME type，如 'image/png', 'image/*', 'audio/*'
  | string[] // MIME type 数组
  | RegExp // 正则表达式，用于匹配 MIME type
  | ((file: File) => boolean); // 自定义判断函数

/**
 * 预设分辨率常量
 */
export const RESOLUTIONS = {
  /** 480p - 720x480 */
  '480P': { width: 720, height: 480 },
  /** 720p - 1280x720 */
  '720P': { width: 1280, height: 720 },
  /** 1080p - 1920x1080 */
  '1080P': { width: 1920, height: 1080 },
  /** 2K - 2560x1440 */
  '2K': { width: 2560, height: 1440 },
  /** 4K - 3840x2160 */
  '4K': { width: 3840, height: 2160 },
  /** 8K - 7680x4320 */
  '8K': { width: 7680, height: 4320 },
} as const;

export type ResolutionPreset = keyof typeof RESOLUTIONS;

/**
 * 文件校验规则
 */
export interface FileUploadRule {
  /**
   * 文件类型约束（可选）
   * - 不配置：规则对所有文件生效
   * - 字符串：MIME type，如 'image/png', 'image/*', 'audio/*'
   * - 字符串数组：多个 MIME type，如 ['image/*', 'video/*']
   * - 正则表达式：匹配 MIME type
   * - 函数：自定义判断逻辑
   */
  fileTypes?: FileTypeMatcher;

  /** 文件最大大小（单位：KB） */
  maxSize?: number;

  /** 文件最小大小（单位：KB） */
  minSize?: number;

  /** 媒体文件最大时长（单位：秒） */
  maxDuration?: number;

  /** 媒体文件最小时长（单位：秒） */
  minDuration?: number;

  /**
   * 最小分辨率限制
   * 支持两种格式：
   * - 预设值：'480P', '720P', '1080P', '2K', '4K', '8K'
   * - 自定义：'宽*高' 格式，如 '300*300', '1920*1080'
   * 只要任一维度小于限制即判定为不合格
   */
  minResolution?: string;

  /**
   * 最大分辨率限制
   * 支持两种格式：
   * - 预设值：'480P', '720P', '1080P', '2K', '4K', '8K'
   * - 自定义：'宽*高' 格式，如 '300*300', '3840*2160'
   * 只要任一维度超过限制即判定为不合格
   */
  maxResolution?: string;

  /**
   * 最小宽高比限制（宽度/高度）
   * 支持两种格式：
   * - 数字：如 0.4 (表示 1:2.5，即高度是宽度的 2.5 倍)
   * - 字符串：如 '1:2.5', '9:16', '3:4'
   * 用于限制图片不能太窄（竖图）
   * 例如：minAspectRatio: 0.4 表示宽高比不能小于 1:2.5
   */
  minAspectRatio?: number | string;

  /**
   * 最大宽高比限制（宽度/高度）
   * 支持两种格式：
   * - 数字：如 2.5 (表示 2.5:1，即宽度是高度的 2.5 倍)
   * - 字符串：如 '2.5:1', '16:9', '21:9'
   * 用于限制图片不能太宽（横图）
   * 例如：maxAspectRatio: 2.5 表示宽高比不能超过 2.5:1
   */
  maxAspectRatio?: number | string;

  /** 自定义错误提示信息（可选，不传则使用默认提示） */
  message?: string;

  /** 自定义校验函数 */
  validator?: ValidatorFunction;

  /** 校验失败提示展示模式：constraint 使用统一约束提示，raw 直接展示原始错误 */
  errorDisplayMode?: ValidationErrorDisplayMode;
}

/**
 * 校验结果
 */
export interface ValidationResult {
  /** 是否通过校验 */
  valid: boolean;
  /** 错误信息列表 */
  errors: string[];
  /** 第一条错误对应的展示模式 */
  firstErrorDisplayMode?: ValidationErrorDisplayMode;
}
